import React, { useState } from 'react';
import { InteractiveReportAction, ViewResultsAction } from '../../components/ActionButtons';
import { Status } from '../PITLSummary';
import { DiscardDisclaimer } from './DiscardDisclaimer';
import { SpecimenCollectionAction } from './SpecimenCollectionAction';
import { RecollectionAction } from './RecollectionAction';
import CancelTest, { canCancelTest, CancelTestModal } from './CancelTest';

const CollectionPackSummary = ({
	gearOrder,
	gearReq,
	initialOrderStatus,
	userInfo,
	setActionResponse,
	setLoading,
	setGearOrder
}) => {
	const [displayCancelModal, setDisplayCancelModal] = useState(false);

	if (!gearReq.canceled && gearReq.accessionDetails?.length) {
		return (
			<AccessionSummary
				gearOrder={gearOrder}
				gearReq={gearReq}
				orderStatus={initialOrderStatus}
				setActionResponse={setActionResponse}
				setLoading={setLoading}
				setGearOrder={setGearOrder}
			/>
		);
	}

	// Req is not accessioned. Show req name and activation actions.
	const requisitionName = gearReq.panelName || gearReq.omsOrderableItemName;
	let reqTitle = requisitionName;
	if (
		canCancelTest({ gearOrder, gearReq }) &&
		initialOrderStatus !== Status.Canceled &&
		initialOrderStatus !== Status.OrderPlaced
	) {
		reqTitle = (
			<>
				{requisitionName}
				{/* The modal needs to be outside of the h4 header for proper styling */}
				<CancelTest gearOrder={gearOrder} gearReq={gearReq} setDisplayCancelModal={setDisplayCancelModal} />
			</>
		);
	}

	let displaySpecimenCollection = canCollectSpecimen(gearOrder, gearReq);

	return (
		<>
			<h4 className="mt-20" style={{ fontWeight: 'inherit' }}>
				{reqTitle}
			</h4>
			{displaySpecimenCollection && (
				<SpecimenCollectionAction
					key={`collect_${gearReq.id}`}
					gearOrder={gearOrder}
					gearRequisition={gearReq}
					userInfo={userInfo}
					setGearOrder={setGearOrder}
					setLoading={setLoading}
				/>
			)}
			{gearReq.canceled && (
				<div style={{ display: 'flex' }}>
					<div>
						<i className="fa fa-warning text-warning ml-5 mr-10" aria-hidden="true" />
					</div>
					<div>
						<div>This test has been canceled.</div>
						<div>
							Your original payment, minus the $40 non-refundable fee, will be refunded to your credit or
							debit card.
						</div>
					</div>
				</div>
			)}
			{displayCancelModal && (
				<CancelTestModal
					setDisplayCancelModal={setDisplayCancelModal}
					requisitionName={requisitionName}
					gearOrderId={gearOrder.id}
					gearReqId={gearReq.id}
					dateOfBirth={gearOrder.patientInfo?.dateOfBirth}
					setGearOrder={setGearOrder}
					setActionResponse={setActionResponse}
					setLoading={setLoading}
				/>
			)}
		</>
	);
};

/**
 * Returns whether or not the Specimen Collection button should be displayed.
 *
 * In a typical order, we wait until a pack is shipped to show the button, but we need to show it
 * before the pack is delivered in case our tracking is not up-to-date.
 * Example: Patient receives pack and wants to activate immediately but we don't update tracking until the next day.
 *
 * If a recollection has happened, show the button as soon as a requisition is created.
 * The tracking logic doesn't work once a pack on the order is recollected. For a recollection, the
 * tracking information is for the new pack (the pack sent for recollection). If we continued to use tracking
 * details to show the buttons, any packs on the order that weren't recollected won't have the button until after
 * the recollection pack is shipped.
 */
const canCollectSpecimen = (gearOrder, gearReq) => {
	if (!gearReq.reqNumber || gearReq.canceled) {
		return false;
	}

	// If any recollection has happened, return true
	if (gearOrder.pitlRecollectionOrderSummary) {
		return true;
	}

	// No recollection has happened. Show the button as soon as the pack is shipped.
	return gearOrder.trackingInformation?.trackingNumberList?.length > 0;
};

/**
 * Shows req-level details for an accessioned pack.
 * This could show multiple products if the pack was split into multiple accessions.
 */
const AccessionSummary = ({ gearOrder, gearReq, orderStatus, setActionResponse, setLoading, setGearOrder }) => {
	return gearReq.accessionDetails.map((accessionDetail) => {
		let accessionActions = getAccessionActions(
			gearOrder,
			gearReq,
			accessionDetail,
			setActionResponse,
			setLoading,
			setGearOrder
		);
		const requisitionName = gearReq.panelName || gearReq.omsOrderableItemName;
		let productTitle = accessionDetail.productName || requisitionName;
		let dueDate = null;
		if (accessionDetail?.dueDate) {
			dueDate = new Date(accessionDetail.dueDate)?.toLocaleDateString();
		}

		let discardDetails = null;
		if (accessionDetail.productName === 'Discarded Specimen') {
			productTitle = (
				<>
					{requisitionName}
					<br />
					<small>Discarded Specimen - View your Results for more information about what went wrong.</small>
				</>
			);
			discardDetails = (
				<DiscardDisclaimer gearOrder={gearOrder} gearReq={gearReq} accessionDetail={accessionDetail} />
			);
		}

		return (
			<div key={accessionDetail.accessionNo}>
				<h4 className="mt-20" style={{ fontWeight: 'inherit' }}>{productTitle}</h4>
				{dueDate && orderStatus === Status.ReceivedByLab && dueDate >= new Date().toLocaleDateString() && (
					<p>
						Your results are expected on or before <b>{dueDate}</b>
					</p>
				)}
				{discardDetails}
				{accessionActions?.length && <div className="btn-toolbar-20">{accessionActions}</div>}
			</div>
		);
	});
};

/**
 * Get the action buttons for a specific accession
 *
 * @param gearOrder The gear order
 * @param gearReq The gear req
 * @param accessionDetail Accession details about the gear req
 * @param setActionResponse A function to pass any messages that need to be displayed
 * @param setLoading A function to call to display a loading shield and spinner
 * @param setGearOrder A function to pass the updated gearOrder back up the tree
 */
const getAccessionActions = (gearOrder, gearReq, accessionDetail, setActionResponse, setLoading, setGearOrder) => {
	if (!accessionDetail) {
		return null;
	}
	const accessionNo = accessionDetail.accessionNo;
	const hasAccessionNo = !!accessionNo;
	const hasDiscardedSpecimens = !!accessionDetail.discardedSpecimen;
	const reportReleased =
		accessionDetail.requisitionStatusId === 3 ||
		accessionDetail.requisitionStatusId === 4 ||
		accessionDetail.requisitionStatusId === 9;
	const hasSTAR = !!accessionDetail.issues?.length;

	let actionButtons = [];
	if (hasDiscardedSpecimens) {
		actionButtons.push(<ViewResultsAction key="results" gearOrder={gearOrder} accessionNo={accessionNo} />);

		if (!hasSTAR) {
			actionButtons.push(
				<RecollectionAction
					key="recollect"
					gearOrder={gearOrder}
					gearReqId={gearReq?.id}
					setActionResponse={setActionResponse}
					setLoading={setLoading}
					setGearOrder={setGearOrder}
				/>
			);
		}
	} else if (hasAccessionNo && !reportReleased) {
		actionButtons.push(
			<div
				key="in-progress"
				className="btn btn-md btn-radius-circle btn-primary disabled"
				style={{ cursor: 'default', textTransform: 'none' }}
			>
				Testing in Progress
			</div>
		);
	} else if (hasAccessionNo && reportReleased) {
		actionButtons.push(<ViewResultsAction key="results" gearOrder={gearOrder} accessionNo={accessionNo} />);
		actionButtons.push(
			<InteractiveReportAction key="interactive-report" gearOrder={gearOrder} accessionDetail={accessionDetail} />
		);
	}

	if (!actionButtons.length) {
		return null;
	}
	return actionButtons;
};

export default CollectionPackSummary;
